import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-bulk-action-bar.css?inline';

/**
 * `<pk-bulk-action-bar>` — bulk-action toolbar for multi-select table rows.
 *
 * Slides in from the top of the surface when `visible` is set. Displays:
 *   - a count region (slotted, or auto-rendered "N selected" from `count`)
 *   - a default slot for action buttons (Delete selected, Export, etc.)
 *   - a built-in cancel/X button that emits `bulk-cancel`
 *
 * Attributes:
 *   - `count`   — numeric; renders "N selected" when no `count` slot is provided.
 *   - `visible` — boolean; when set, the bar is shown (in DOM + visually).
 *                 When unset, the bar is hidden via shadow render so it
 *                 collapses out of the layout entirely.
 *
 * Slots:
 *   - default — action buttons cluster.
 *   - `count` — override the auto-rendered count text.
 *
 * Events:
 *   - `bulk-cancel` — fired when the X is clicked.
 */
export class PkBulkActionBar extends HTMLElement {
    static readonly tagName = 'pk-bulk-action-bar';

    static get observedAttributes(): string[] {
        return ['count', 'visible'];
    }

    private readonly _root: ShadowRoot;
    private _hasCountSlotContent = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _onCancel = (): void => {
        this.dispatchEvent(new CustomEvent('bulk-cancel', { bubbles: true, composed: true }));
    };

    private readonly _onCountSlotChange = (e: Event): void => {
        const slot = e.target as HTMLSlotElement;
        const has  = slot.assignedNodes({ flatten: true }).some((n) => {
            if (n.nodeType === Node.ELEMENT_NODE) return true;
            return (n.textContent ?? '').trim().length > 0;
        });
        if (has !== this._hasCountSlotContent) {
            this._hasCountSlotContent = has;
            this.render();
        }
    };

    private _count(): number {
        const raw = Number(this.getAttribute('count') ?? '0');
        return Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : 0;
    }

    private template(): TemplateResult {
        const visible       = this.hasAttribute('visible');
        const count         = this._count();
        const showAutoCount = !this._hasCountSlotContent;

        if (!visible) {
            return html`<style>${styles}</style>`;
        }

        return html`
            <style>${styles}</style>
            <div class="bar" role="region" aria-label="Bulk actions">
                <div class="count">
                    <slot name="count" @slotchange=${this._onCountSlotChange}></slot>
                    ${showAutoCount ? html`<span class="count-text">${count} selected</span>` : ''}
                </div>
                <div class="actions">
                    <slot></slot>
                </div>
                <button
                    type="button"
                    class="cancel-btn"
                    aria-label="Cancel selection"
                    @click=${this._onCancel}
                >&#x2715;</button>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkBulkActionBar.tagName)) {
    customElements.define(PkBulkActionBar.tagName, PkBulkActionBar);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-bulk-action-bar': PkBulkActionBar;
    }
}
